import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { useRejectNegotiation } from "../../hooks/useRejectNegotiation";
import { SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { rejectNegotiationSchema, RejectNegotiationValidationSchema } from "../../schemas/rejectNegotiation.schema";
import TextArea from "@/common/components/TextArea";
import { NegotiationCounterValidationSchema } from "../../schemas/negotiationCounter.schema";
import { Tooltip } from "react-tooltip";

const RejectNegotiationModal = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const negotiationData = queryClient.getQueryData(['negotiations', id])

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isOpen, setIsOpen] = useState(false);

  const { mutate: rejectNegotiation, isError: rejectNegotiationFailed } = useRejectNegotiation({
    negotiationId: id,
    onSuccess: () => {
      setIsOpen(false);
      setMessages(['Negociação recusada com sucesso']);
    },
    onError: (error) => {
      setMessages(error.response?.data.errors?.base || ['Ocorreu um erro ao recusar a negociação']);
    },
  });

  const { watch, formState: { disabled: negotiationCounterDisabled } } = useFormContext<NegotiationCounterValidationSchema>();

  const watchNegotiationProducts = watch('negotiation_products_attributes');

  const hasSomeCounter = watchNegotiationProducts.some((negotiationProduct) => negotiationProduct.desired_action === 'counter');

  const { handleSubmit, register, formState: { errors } } = useForm<RejectNegotiationValidationSchema>({
    resolver: zodResolver(rejectNegotiationSchema),
  });

  const onSubmit: SubmitHandler<RejectNegotiationValidationSchema> = (data) => rejectNegotiation(data);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={hasSomeCounter || !negotiationData}
        Trigger={
          !negotiationCounterDisabled ? (
            <Button
              variant="error"
              disabled={hasSomeCounter || !negotiationData}
              data-tooltip-id="reject-negotiation-tooltip"
            >
              Recusar negociação
            </Button>
          ) : <></>
        }
        title="Recusar"
        subtitle="Preencha o motivo para recusa da negociação"
        titleClass="text-2xl"
        subtitleClassName="mt-1 text-white-400"
        body={(
          <div className="flex flex-col gap-4 text-white-800">
            <TextArea
              label="Justificativa"
              markAsRequired
              rows={4}
              {...register('observation')}
              errorMessage={errors.observation?.message || undefined}
            />
          </div>
        )}
        useCancellableFooter
        cancelButtonText="Voltar"
        confirmButtonText="Recusar negociação"
        cancelButtonClassName="bg-white-200"
        className="rounded-lg overflow-hidden"
        onConfirm={handleSubmit(onSubmit)}
      />

      {hasSomeCounter && (
        <Tooltip id="reject-negotiation-tooltip">
          Remova a contraproposta para poder recusar a negociação
        </Tooltip>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={rejectNegotiationFailed}
          onClose={() => queryClient.invalidateQueries({ queryKey: ['negotiations', id] })}
        />
      )}
    </>
  );
};

export default RejectNegotiationModal;
